// Singleton audio controller — ensures only one sound plays at a time.
// Call play() to stop whatever is currently playing and start a new sound.

let current = null;
// Tracks a pending onEnded callback so that play() can invoke it when it
// preempts a still-playing sound. Without this, pause() does not fire the
// 'ended' event, so the { once: true } listener never runs and any Addy
// animation waiting on audio end stays frozen indefinitely.
let pendingOnEnded = null;

const audioManager = {
  play(src, volume = 1) {
    // Invoke any pending onEnded callback before replacing the current audio.
    // This covers the case where play() preempts before the sound ends naturally.
    if (pendingOnEnded) {
      const cb = pendingOnEnded;
      pendingOnEnded = null;
      cb();
    }
    if (current) {
      current.pause();
      current.currentTime = 0;
    }
    try {
      current = new Audio(src);
      current.volume = volume;
      current.play().catch(() => {});
    } catch (e) {
      current = null;
    }
  },

  // Attach a one-shot callback to fire when the current sound ends.
  // Must be called after play() so current is set.
  // If play() is called again before the sound ends naturally, the callback
  // is invoked by play() rather than the 'ended' event.
  // The event listener checks pendingOnEnded === callback before firing so
  // that a play() preemption (which clears pendingOnEnded to null) prevents
  // the listener from double-calling an already-invoked callback.
  onEnded(callback) {
    if (current) {
      pendingOnEnded = callback;
      current.addEventListener('ended', () => {
        if (pendingOnEnded === callback) {
          pendingOnEnded = null;
          callback();
        }
      }, { once: true });
    }
  },

  stop() {
    if (current) {
      current.pause();
      current.currentTime = 0;
      current = null;
    }
    // Intentional stop — discard the pending callback rather than invoking it.
    pendingOnEnded = null;
  },
};

export default audioManager;
